import { useCallback, useEffect, useMemo, useState } from 'react';

import { authService } from '@/services/auth.service';
import type { AuthScope } from '@/types/auth';
import type { PasswordPolicy } from '@/types/provider-auth-settings';

interface UsePasswordLoginAvailabilityOptions {
  tenantSlug?: string;
  skip?: boolean;
  defaultEnabled?: boolean;
}

interface UsePasswordLoginAvailabilityResult {
  enabled: boolean;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<boolean>;
  policy: Partial<PasswordPolicy> | null;
}

/**
 * Lightweight hook that resolves whether password login is enabled for the
 * given auth scope. Falls back to the provided default to avoid blocking login
 * flows when the backend endpoint is unavailable.
 */
export function usePasswordLoginAvailability(
  scope: AuthScope,
  options: UsePasswordLoginAvailabilityOptions = {},
): UsePasswordLoginAvailabilityResult {
  const { tenantSlug, skip = false, defaultEnabled = true } = options;
  const [enabled, setEnabled] = useState<boolean>(defaultEnabled);
  const [isLoading, setIsLoading] = useState<boolean>(!skip);
  const [error, setError] = useState<Error | null>(null);
  const [policy, setPolicy] = useState<Partial<PasswordPolicy> | null>(null);

  const normalizedTenant = useMemo(() => tenantSlug?.trim() || undefined, [tenantSlug]);

  const fetchAvailability = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (skip) {
        setIsLoading(false);
        setPolicy(null);
        return defaultEnabled;
      }

      if (!opts.silent) {
        setIsLoading(true);
      }

      try {
        const result = await authService.getPasswordLoginSettings(scope, {
          tenantSlug: normalizedTenant,
        });
        setEnabled(result.enabled);
        setPolicy(result.policy ?? null);
        setError(null);
        return result.enabled;
      } catch (err) {
        console.error('Failed to load password login availability', err);
        setEnabled(defaultEnabled);
        setPolicy(null);
        const normalizedError =
          err instanceof Error ? err : new Error('Failed to load password login availability');
        setError(normalizedError);
        return defaultEnabled;
      } finally {
        setIsLoading(false);
      }
    },
    [defaultEnabled, normalizedTenant, scope, skip],
  );

  useEffect(() => {
    let cancelled = false;

    if (skip) {
      setIsLoading(false);
      setPolicy(null);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      const result = await fetchAvailability({ silent: false });
      if (!cancelled) {
        setEnabled(result);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchAvailability, skip]);

  const refresh = useCallback(async () => fetchAvailability({ silent: false }), [fetchAvailability]);

  return {
    enabled,
    isLoading,
    error,
    refresh,
    policy,
  };
}
